import React, { useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  type: ToastType;
  title?: string;
  message: string;
  onClose: () => void;
  className?: string;
  autoDismiss?: boolean;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  type,
  title,
  message,
  onClose,
  className = '',
  autoDismiss = true,
  duration = 5000
}) => {
  useEffect(() => {
    if (autoDismiss) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, duration, onClose]);
  const getToastConfig = (type: ToastType) => {
    const configs = {
      success: {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconColor: 'text-green-400',
        titleColor: 'text-green-800',
        messageColor: 'text-green-700',
        buttonBg: 'bg-green-50',
        buttonHover: 'hover:bg-green-100',
        buttonText: 'text-green-400',
        buttonHoverText: 'hover:text-green-500',
        focusRing: 'focus:ring-green-500',
        focusRingOffset: 'focus:ring-offset-green-50',
        icon: CheckCircle,
        defaultTitle: 'Success'
      },
      error: {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconColor: 'text-red-400',
        titleColor: 'text-red-800',
        messageColor: 'text-red-700',
        buttonBg: 'bg-red-50',
        buttonHover: 'hover:bg-red-100',
        buttonText: 'text-red-400',
        buttonHoverText: 'hover:text-red-500',
        focusRing: 'focus:ring-red-500',
        focusRingOffset: 'focus:ring-offset-red-50',
        icon: AlertCircle,
        defaultTitle: 'Error'
      },
      warning: {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        iconColor: 'text-yellow-400',
        titleColor: 'text-yellow-800',
        messageColor: 'text-yellow-700',
        buttonBg: 'bg-yellow-50',
        buttonHover: 'hover:bg-yellow-100',
        buttonText: 'text-yellow-400',
        buttonHoverText: 'hover:text-yellow-500',
        focusRing: 'focus:ring-yellow-500',
        focusRingOffset: 'focus:ring-offset-yellow-50',
        icon: AlertTriangle,
        defaultTitle: 'Warning'
      },
      info: {
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-800',
        messageColor: 'text-blue-700',
        buttonBg: 'bg-blue-50',
        buttonHover: 'hover:bg-blue-100',
        buttonText: 'text-blue-400',
        buttonHoverText: 'hover:text-blue-500',
        focusRing: 'focus:ring-blue-500',
        focusRingOffset: 'focus:ring-offset-blue-50',
        icon: Info,
        defaultTitle: 'Information'
      }
    };
    return configs[type];
  };

  const config = getToastConfig(type);
  const Icon = config.icon;
  const displayTitle = title || config.defaultTitle;

  return (
    <div className={`p-4 ${config.bgColor} border ${config.borderColor} rounded-md ${className}`}>
      <div className="flex">
        <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0`} />
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${config.titleColor}`}>
            {displayTitle}
          </h3>
          <p className={`text-sm ${config.messageColor} mt-1`}>
            {message}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`ml-3 flex-shrink-0 rounded-md ${config.buttonBg} p-1.5 ${config.buttonText} ${config.buttonHover} ${config.buttonHoverText} focus:outline-none focus:ring-2 ${config.focusRing} focus:ring-offset-2 ${config.focusRingOffset}`}
        >
          <span className="sr-only">Dismiss</span>
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
